import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ListTree } from 'lucide-react';
import apiReq from 'core/api';
import DocumentPreviewDialog from 'components/DocumentPreviewDialog';
import type { ContextFileSelection } from 'types/contextDocuments';

interface ContextFile {
  id: string;
  name: string;
  url: string;
  created_at: string;
  content_length: number;
}

interface ContextFilesSelectProps {
  projectId: string | number;
  selected: ContextFileSelection[];
  onChange: (selections: ContextFileSelection[]) => void;
}

const ContextFilesSelect = ({ projectId, selected, onChange }: ContextFilesSelectProps) => {
  // Пикер общей информации работает с файлами уровня проекта (target_type: 'project'),
  // поэтому chat_id передавать НЕ нужно — иначе бэкенд вернёт документы уровня чата,
  // и проектные файлы общей информации пропадут. Ключ кэша project-scoped (как в FilesTab),
  // чтобы не пересекаться с chat-scoped выборкой AttachmentChipsList.
  const { data: files = [], isLoading } = useQuery({
    queryKey: ['contextDocuments', projectId],
    queryFn: async () => {
      const result = await apiReq<ContextFile[]>(
        `projects/context-documents?project_id=${String(projectId)}`,
        'GET'
      );
      return result || [];
    },
    enabled: !!projectId,
  });

  const [previewFile, setPreviewFile] = useState<ContextFile | null>(null);

  // Автоматическая очистка несуществующих файлов из выбранных
  useEffect(() => {
    if (Array.isArray(files) && files.length > 0 && selected.length > 0) {
      const existingFileIds = new Set(files.map((file: ContextFile) => file.id));
      const validSelected = selected.filter((s) => existingFileIds.has(s.id));
      if (validSelected.length !== selected.length) {
        onChange(validSelected);
      }
    } else if (Array.isArray(files) && files.length === 0 && selected.length > 0) {
      onChange([]);
    }
  }, [files, selected, onChange]);

  const handleToggleFile = (fileId: string) => {
    const isSelected = selected.some((s) => s.id === fileId);
    onChange(isSelected ? selected.filter((s) => s.id !== fileId) : [...selected, { id: fileId }]);
  };

  const handleApplyHeaders = (fileId: string, headers: string[] | undefined) => {
    onChange(
      selected.map((s) =>
        s.id === fileId ? (headers && headers.length > 0 ? { id: s.id, headers } : { id: s.id }) : s
      )
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!Array.isArray(files) || files.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
        Файлы не загружены
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {files.map((file: ContextFile) => {
        const selection = selected.find((s) => s.id === file.id);
        const headersCount = selection?.headers?.length ?? 0;
        return (
          <Box key={file.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!selection}
                  onChange={() => handleToggleFile(file.id)}
                />
              }
              label={
                <Typography variant="body2" noWrap sx={{ fontSize: '0.875rem' }}>
                  {file.name}
                  {headersCount > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="primary"
                      sx={{ ml: 0.5, whiteSpace: 'nowrap' }}
                    >
                      · {headersCount} разд.
                    </Typography>
                  )}
                </Typography>
              }
              sx={{
                margin: 0,
                flex: 1,
                minWidth: 0,
                // Без minWidth: 0 у label длинное имя файла не обрезается
                // и наезжает на кнопку превью справа
                '& .MuiFormControlLabel-label': { minWidth: 0 },
              }}
            />
            {selection && (
              <Tooltip title="Выбрать разделы документа">
                <IconButton
                  size="small"
                  onClick={() => setPreviewFile(file)}
                  sx={{
                    flexShrink: 0,
                    color: 'primary.main',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: (theme) => `${theme.palette.primary.main}14`,
                    },
                  }}
                >
                  <ListTree size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
      <DocumentPreviewDialog
        open={!!previewFile}
        documentId={previewFile?.id ?? null}
        documentName={previewFile?.name}
        selectedHeaders={selected.find((s) => s.id === previewFile?.id)?.headers}
        onApply={(headers) => previewFile && handleApplyHeaders(previewFile.id, headers)}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
};

export default ContextFilesSelect;
